export type Language = 'pl' | 'en';

export interface VbaShowcaseStep {
  title: string;
  caption: string;
}

export interface VbaShowcaseContent {
  kind: 'vba-configurator';
  intro: string;
  steps: VbaShowcaseStep[];
  ui: {
    deviceType: string;
    capacity: string;
    control: string;
    controlManual: string;
    controlElectric: string;
    lockedHint: string;
    flowParams: string;
    flowEngine: string;
    flowOutput: string;
    flowPrice: string;
    flowLocks: string;
    docHeader: string;
    docTable: string;
    docImage: string;
    docTotal: string;
    deviceOption1: string;
    deviceOption2: string;
    capacityOption1: string;
    capacityOption2: string;
  };
}

export interface ProjectItem {
  id: string;
  title: string;
  year?: string;
  type: string;
  role: string;
  description: string;
  liveUrl?: string;
  image?: string;
  secondaryImage?: string;
  sublinks?: { label: string; url: string }[];
  showcase?: VbaShowcaseContent;
  summary?: string;
  note?: string;
}

export interface TimelineItem {
  type: 'work' | 'education';
  title: string;
  subtitle: string;
  date: string;
  points: string[];
}

export interface ExpertiseItem {
  iconType: 'code' | 'server' | 'chart' | 'hosting';
  title: string;
  description: string;
  techStack: string[];
}

export interface FeaturedImage {
  src: string;
  alt: string;
  caption: string;
}

export interface FeaturedLink {
  label: string;
  url: string;
  kind: 'primary' | 'secondary' | 'github';
}

export interface FeaturedProjectContent {
  id: string;
  eyebrow: string;
  title: string;
  pitch: string;
  problemHeading: string;
  problem: string;
  solutionHeading: string;
  solution: string;
  roleLabel: string;
  role: string;
  featuresHeading: string;
  features: string[];
  techLabel: string;
  tech: string[];
  newTabHint: string;
  galleryLabel: string;
  links: FeaturedLink[];
  images: FeaturedImage[];
}

export interface PortfolioContent {
  nav: {
    about: string;
    expertise: string;
    history: string;
    projects: string;
    contact: string;
    downloadCv: string;
    cvUrl: string;
  };
  main: {
    name: string;
    roles: string;
    location: string;
    aboutShort: string;
    downloadCv: string;
    cvUrl: string;
    seeProjects: string;
    photoUrl: string;
    social: {
      github: string;
      linkedin: string;
      email: string;
    };
  };
  expertise: {
    heading: string;
    subtitle: string;
    techStackLabel: string;
    items: ExpertiseItem[];
  };
  featured: FeaturedProjectContent[];
  timeline: {
    heading: string;
    items: TimelineItem[];
  };
  projects: {
    heading: string;
    subtitle: string;
    cursorLabel: string;
    viewProject: string;
    roleLabel: string;
    scopeLabel: string;
    items: ProjectItem[];
  };
  contact: {
    heading: string;
    subheading: string;
    nameLabel: string;
    namePlaceholder: string;
    nameError: string;
    contactLabel: string;
    contactPlaceholder: string;
    contactError: string;
    messageLabel: string;
    messagePlaceholder: string;
    messageError: string;
    sendButton: string;
    successMessage: string;
    errorMessage: string;
    directContact: string;
  };
  footer: {
    copyright: string;
  };
}

export const content: Record<Language, PortfolioContent> = {
  pl: {
    nav: {
      about: 'O mnie',
      expertise: 'Umiejętności',
      history: 'Doświadczenie',
      projects: 'Realizacje',
      contact: 'Kontakt',
      downloadCv: 'Pobierz CV',
      cvUrl: '/projects/CV_Pawel_PL.pdf',
    },
    main: {
      name: 'Paweł Ryś',
      roles: 'Web Developer · Next.js • WordPress • SEO',
      location: 'Okolice Bielska-Białej',
      aboutShort: 'Tworzę nowoczesne, zoptymalizowane strony internetowe oraz narzędzia biznesowe, łącząc programowanie z analityką, SEO i automatyzacją procesów.',
      downloadCv: 'Pobierz CV (PL)',
      cvUrl: '/projects/CV_Pawel_PL.pdf',
      seeProjects: 'Zobacz realizacje',
      photoUrl: '/photo.jpg',
      social: {
        github: 'https://github.com/pawel799',
        linkedin: '',
        email: 'pawelrys175@gmail.com',
      },
    },
    expertise: {
      heading: 'Umiejętności',
      subtitle: 'Technologie i obszary specjalizacji',
      techStackLabel: 'Technologie:',
      items: [
        {
          iconType: 'code',
          title: 'Web Development',
          description: 'Projektowanie i programowanie nowoczesnych, szybkich stron oraz aplikacji webowych z naciskiem na czysty kod, responsywność i optymalizację.',
          techStack: ['HTML', 'CSS', 'JavaScript', 'TypeScript', 'React', 'Next.js', 'Three.js / React Three Fiber', 'Tailwind CSS', 'WordPress', 'Decap CMS', 'PHP', 'MySQL', '.NET', 'SQL Server', 'Cloudflare'],
        },
        {
          iconType: 'server',
          title: 'Automatyzacje i Wsparcie IT',
          description: 'Automatyzacja procesów biznesowych, integracje API, rozwój narzędzi wewnętrznych oraz kompleksowa administracja systemami i sieciami.',
          techStack: ['Make (automatyzacje)', 'VBA', 'Systemy InsERT GT / Nexo', 'Sieci, monitoring IP, kopie zapasowe'],
        },
        {
          iconType: 'chart',
          title: 'Digital Marketing i SEO',
          description: 'Skuteczne pozycjonowanie stron (w tym lokalne SEO), analityka, wdrażanie danych strukturalnych oraz prowadzenie kampanii reklamowych Google Ads.',
          techStack: ['SEO', 'Lokalne SEO', 'Google Ads', 'Dane strukturalne JSON-LD', 'Analityka', 'Hreflang / Sitemap'],
        },
        {
          iconType: 'hosting',
          title: 'Hosting i serwery',
          description: 'Konfiguracja hostingu, domen i certyfikatów, przenoszenie stron między serwerami oraz automatyczne wdrożenia.',
          techStack: ['Linux', 'Windows Server', 'Plesk', 'cPanel', 'DNS / SSL', 'GitHub Actions'],
        },
      ],
    },
    featured: [{
      id: 'featured',
      eyebrow: 'Projekt wyróżniony',
      title: 'System ofertowania kuchni 3D',
      pitch: 'Oferta kuchni, którą klient ogląda w 3D w przeglądarce, zamiast PDF-a i zrzutów ekranu.',
      problemHeading: 'Problem',
      problem:
        'Studia i salony meblowe wysyłają klientom oferty jako PDF-y i zrzuty z programu projektowego. Klient nie może sam porównać materiałów, każda zmiana frontu czy blatu to kolejna wiadomość i nowa wycena, a projektant nie wie, czy oferta w ogóle została otwarta.',
      solutionHeading: 'Rozwiązanie',
      solution:
        'Aplikacja webowa, w której projektant przygotowuje ofertę w panelu i wysyła klientowi link. Klient otwiera kuchnię w 3D na telefonie albo komputerze, zmienia fronty, blat i okucia, od razu widzi cenę i zostawia uwagi. Projektant widzi w panelu otwarcia, uwagi i status oferty.',
      roleLabel: 'Moja rola',
      role: 'Zaprojektowałem i wykonałem całość: interfejs panelu i strony oferty, scenę 3D, API na Cloudflare Pages Functions z zapisem w Workers KV oraz wdrożenie.',
      featuresHeading: 'Najważniejsze funkcje',
      features: [
        'Panel ofert ze statusami (szkic, wysłana, otwarta, poprawki, zaakceptowana), wyszukiwarką, unieważnianiem linku i usuwaniem oferty.',
        'Link dla klienta z losowym adresem, opcjonalnym PIN-em i datą ważności; cenę można ukryć osobno dla każdej oferty.',
        'Konfigurator 3D z wymianą frontów, blatu i okuć oraz ceną przeliczaną na żywo.',
        'Uwagi klienta przy ofercie, licznik otwarć i historia zdarzeń w panelu.',
        'Dane firmy po NIP-ie z wykazu podatników VAT (MF) i API KRS.',
        'Szablon maila z ofertą w HTML, zgodny z popularnymi programami pocztowymi.',
      ],
      techLabel: 'Stack',
      tech: ['Next.js 14', 'TypeScript', 'React Three Fiber', 'drei', 'postprocessing', 'Tailwind CSS', 'Cloudflare Pages Functions', 'Workers KV'],
      newTabHint: '(otwiera się w nowej karcie)',
      galleryLabel: 'Zrzuty ekranu systemu ofertowania',
      links: [
        { label: 'Zobacz stronę', url: 'https://system-ofertowania.pages.dev', kind: 'primary' },
        { label: 'Przykładowa oferta 3D', url: 'https://system-ofertowania.pages.dev/oferta/demo', kind: 'secondary' },
        { label: 'GitHub', url: 'https://github.com/pawel799/System-ofertowania-', kind: 'github' },
      ],
      images: [
        {
          src: '/projects/system-ofertowania/oferta-3d.webp',
          alt: 'Oferta u klienta: kuchnia w 3D z paskiem wyboru frontów, blatu i okuć oraz ceną netto',
          caption: 'Oferta u klienta: kuchnia w 3D, wybór materiałów i cena na żywo',
        },
        {
          src: '/projects/system-ofertowania/panel.webp',
          alt: 'Panel ofert: lista ofert, podgląd 3D i link dla klienta z datą ważności i gotową wiadomością',
          caption: 'Panel: oferta, link dla klienta i gotowa wiadomość',
        },
        {
          src: '/projects/system-ofertowania/mail.webp',
          alt: 'Mail z ofertą: podsumowanie materiałów, szacunek ceny i przycisk do kuchni w 3D',
          caption: 'Mail z ofertą i przyciskiem do kuchni w 3D',
        },
      ],
    },
    {
      id: 'featured-rox',
      eyebrow: 'Projekt wyróżniony',
      title: 'ROX Group: strona grupy i 4 strony spółek',
      pitch: 'Pięć stron firmowych w jednym projekcie, z panelem, w którym klient sam zmienia treści.',
      problemHeading: 'Problem',
      problem:
        'Grupa ROX ma cztery spółki o różnych profilach (handel, wynajem sprzętu, produkcja aluminium, logistyka). Każda potrzebowała własnej strony pod swoją domeną, a całość miała wyglądać spójnie i dać się edytować bez programisty.',
      solutionHeading: 'Rozwiązanie',
      solution:
        'Jeden projekt Next.js ze statycznym eksportem, który buduje stronę grupy i cztery strony spółek. Treści klient edytuje w panelu /admin (Decap CMS). Strona działała najpierw na Cloudflare Pages, potem przeniosłem ją na hosting klienta z automatycznym wdrożeniem przez GitHub Actions.',
      roleLabel: 'Moja rola',
      role: 'Wykonałem całość: układ i wygląd stron, konfigurację panelu treści, formularz kontaktowy, wdrożenie oraz przeniesienie na hosting klienta razem z domenami.',
      featuresHeading: 'Najważniejsze elementy',
      features: [
        'Strona grupy i cztery strony spółek (ROX Global, ROX Rent, ROX Aluminium, FLS Global) w jednym repozytorium.',
        'Decap CMS: klient edytuje treści w /admin, logowanie przez GitHub OAuth.',
        'Wdrożenie na hosting klienta przez FTP w GitHub Actions po każdej zmianie w repozytorium.',
        'Osobne domeny spółek: aliasy obsłużone regułami rewrite w Apache, stare adresy przekierowane 301.',
        'Formularz kontaktowy w PHP z wysyłką przez SMTP (PHPMailer) i ochroną Cloudflare Turnstile.',
      ],
      techLabel: 'Stack',
      tech: ['Next.js', 'React', 'TypeScript', 'Tailwind CSS', 'Decap CMS', 'PHP', 'GitHub Actions'],
      newTabHint: '(otwiera się w nowej karcie)',
      galleryLabel: 'Zrzuty ekranu stron ROX Group',
      links: [
        { label: 'Zobacz stronę', url: 'https://gruparox.com', kind: 'primary' },
        { label: 'ROX Global', url: 'https://roxglobal.pl', kind: 'secondary' },
        { label: 'ROX Rent', url: 'https://roxrent.pl', kind: 'secondary' },
        { label: 'ROX Aluminium', url: 'https://roxaluminium.pl', kind: 'secondary' },
        { label: 'FLS Global', url: 'https://fls-global.pl', kind: 'secondary' },
      ],
      images: [
        { src: '/projects/rox-group/gruparox.webp', alt: 'Strona główna ROX Group: hasło grupy i kafelki czterech spółek', caption: 'Strona grupy z wyborem spółki' },
        { src: '/projects/rox-group/roxrent.webp', alt: 'Strona ROX Rent: wypożyczalnia maszyn budowlanych z przyciskiem rezerwacji', caption: 'ROX Rent' },
        { src: '/projects/rox-group/roxaluminium.webp', alt: 'Strona ROX Aluminium: zdjęcie budynku z fasadą aluminiową', caption: 'ROX Aluminium' },
        { src: '/projects/rox-group/admin-edycja.webp', alt: 'Panel Decap CMS: formularz edycji podstrony z polami tytułu, ścieżki, opisu SEO i spółki oraz podglądem', caption: 'Edycja podstrony w panelu /admin (Decap CMS)' },
      ],
    }],
    timeline: {
      heading: 'Doświadczenie i Edukacja',
      items: [
        {
          type: 'work',
          title: 'Web Developer / Marketing',
          subtitle: 'Grupa spółek, m.in. ROX Group',
          date: '09/2026 – obecnie',
          points: [
            'Strony internetowe i obecność w sieci dla grupy spółek, w tym ROX Group: Next.js + Decap CMS (klient sam edytuje treści).',
            'Przeniesienie stron na hosting klienta (Plesk) z wdrożeniem przez GitHub Actions (FTP).',
            'Przekierowania 301 dla około 10 domen, certyfikaty SSL, formularz kontaktowy z wysyłką przez SMTP.',
          ],
        },
        {
          type: 'work',
          title: 'DS System',
          subtitle: '06/2025 – 08/2026',
          date: '06/2025 – 08/2026',
          points: [
            'Rozwój i utrzymanie stron B2B oraz narzędzi firmowych (w tym konfigurator ofert w VBA): m.in. model 3D produktu (Blender → Three.js z kompresją Draco), wyszukiwarka Fuse.js, dane strukturalne JSON-LD (Product), hreflang, sitemap; Next.js na Cloudflare Workers, Resend, Cloudflare Turnstile.',
            'Automatyzacje w Make: scenariusz pobiera zdarzenia z Google Calendar i wysyła klientom przypomnienia serwisowe przez API SMS.',
            'Wsparcie IT: obsługa urządzeń sieciowych (m.in. systemy mesh), naprawa i konfiguracja drukarek.',
            'Pomoc w obsłudze programów InsERT oraz konfiguracja i migracja danych z InsERT GT do Nexo.',
          ],
        },
        {
          type: 'work',
          title: 'DUDIKOM - Pogotowie Komputerowe',
          subtitle: 'Staż wakacyjny',
          date: '07/2024',
          points: [
            'Informatyka i sieci: administracja systemami IT, programowanie, zarządzanie sieciami, konfiguracja sieci kamer IP i systemów monitoringu.',
            'Strony i bazy danych: tworzenie i optymalizacja stron (HTML, CSS, WordPress), zarządzanie bazami SQL, bezpieczeństwo IT i kopie zapasowe.',
          ],
        },
        {
          type: 'work',
          title: 'SILV Systemy Informatyczne',
          subtitle: 'Praktyki',
          date: '04/2024',
          points: [
            'Tworzenie aplikacji w środowisku .NET, praca z bazami danych SQL Server.',
          ],
        },
        {
          type: 'work',
          title: 'M3.NET Sp. z o.o. (Bielsko-Biała)',
          subtitle: 'Praktyki',
          date: '10/2023',
          points: [
            'Udział w pracach związanych z instalacją i konfiguracją infrastruktury światłowodowej.',
          ],
        },
        {
          type: 'work',
          title: 'Shell',
          subtitle: 'Młodszy pracownik obsługi',
          date: '04/2023 – 06/2025',
          points: [
            'Obsługa klientów i kasy fiskalnej, pomoc w dostawach, MS Office, zarządzanie produktami i stanem magazynowym.',
          ],
        },
        {
          type: 'education',
          title: 'Uniwersytet Bielsko-Bialski',
          subtitle: 'Kierunek: Informatyka',
          date: '09/2025 – w trakcie',
          points: [
            'Przedmioty m.in.: algorytmy i struktury danych, bazy danych (SQL), systemy operacyjne, architektura komputerów, matematyka dyskretna, algebra, rachunek prawdopodobieństwa i statystyka, fizyka, UI/UX.',
          ],
        },
        {
          type: 'education',
          title: 'Technik informatyk',
          subtitle: 'Certyfikaty zawodowe INF.02 i INF.03',
          date: 'Edukacja',
          points: [
            'Uzyskanie tytułu technika informatyka oraz kwalifikacji zawodowych INF.02 i INF.03.',
          ],
        },
      ],
    },
    projects: {
      heading: 'Realizacje',
      subtitle: 'Wybrane projekty stron internetowych i narzędzi biznesowych',
      cursorLabel: 'Zobacz',
      viewProject: 'Zobacz online',
      roleLabel: 'Moja rola:',
      scopeLabel: 'Zakres i stack:',
      items: [
        {
          id: 'ds-system',
          title: 'DS System',
          type: 'Serwis B2B / Next.js',
          role: 'Pełne wykonanie strony B2B: architektura, wersje językowe, SEO techniczne, wdrożenie na Cloudflare Workers.',
          description:
            'Producent przemysłowych chwytaków próżniowych, żurawi i systemów magazynowych (CE, Polska). Strona wielojęzyczna (EN, PL, CZ) z katalogiem produktów i formularzami ofertowymi. Model 3D produktu wykonany w Blenderze, wyrenderowany na stronie w Three.js z kompresją geometrii Draco (Google) dla szybkiego ładowania. Dane strukturalne: JSON-LD (Product schema), hreflang, sitemap. Wyszukiwarka: Fuse.js (fuzzy search), podświetlanie wyników, nawigacja z klawiatury, historia wyszukiwań (localStorage). Stack: Next.js na Cloudflare Workers, Resend (formularz kontaktowy), Cloudflare Turnstile (anti-spam).',
          liveUrl: 'https://dssystem-machinery.com',
          image: '/projects/dssystem.jpg',
        },
        {
          id: 'igosport',
          title: 'IGOSPORT',
          type: 'Sklep biegowy / Lokalne SEO · Next.js + Tailwind',
          role: 'Projekt i wykonanie strony wizytówki sklepu biegowego z naciskiem na lokalne SEO.',
          description:
            'Sklep biegowy w Bielsku-Białej: analiza chodu i dobór obuwia (Saucony, Brooks, Diadora, Tecnica). Strona główna, Eventy, Kontakt, lokalne SEO (Bielsko-Biała), opinie Google, mapa dojazdu. Stack: Next.js, Tailwind CSS. Wyniki Google Search Console (27.05–wrz. 2026): 490 kliknięć, 8,84 tys. wyświetleń, CTR 5,5%, średnia pozycja 5,8 — wyraźny wzrost kliknięć i wyświetleń od początku września 2026.',
          liveUrl: 'https://igosport.pl',
          image: '/projects/igosport.jpg',
          secondaryImage: '/projects/google-search.png',
          summary: 'Strona sklepu w Next.js i Tailwind CSS oraz lokalne SEO dla Bielska-Białej.',
          note: 'Google Search Console, widok 12 miesięcy: 490 kliknięć, 8,84 tys. wyświetleń, średnia pozycja 5,8. Wzrost po wdrożeniu lokalnego SEO i optymalizacji treści.',
        },
        {
          id: 'vba-configurator',
          title: 'Konfigurator VBA - DS System',
          type: 'Narzędzie wewnętrzne / VBA',
          role: 'Projekt i implementacja automatycznego konfiguratora ofert w VBA dla DS System.',
          description:
            'Konfigurator ofertowy w Excel VBA, zintegrowany z Wordem. Handlowiec wybiera typ urządzenia i parametry (nośność, wymiary, zasilanie, sterowanie) w formularzu, a program sam dobiera ceny z bazy, nalicza rabaty i VAT, po czym generuje gotową ofertę w Wordzie — wypełnia tabele cenowe, wstawia zdjęcie techniczne dopasowane do konfiguracji i liczy podsumowania. Zawiera blokady logiczne, które uniemożliwiają wybór wzajemnie wykluczających się opcji (np. sterowania manualnego razem z napędem elektrycznym), obsługuje przewalutowanie i generowanie ofert w innym języku oraz zapis i wczytywanie zapisanych konfiguracji. Skrócił czas przygotowania oferty z kilkunastu–kilkudziesięciu minut do około 30 sekund.',
          showcase: {
            kind: 'vba-configurator',
            intro:
              'Konfigurator ofertowy w Excel VBA, zintegrowany z Wordem: od wyboru parametrów w formularzu do gotowej oferty w Wordzie.',
            steps: [
              {
                title: 'Konfiguracja',
                caption:
                  'Handlowiec wybiera typ urządzenia i parametry (nośność, wymiary, zasilanie, sterowanie) w formularzu. Pola są tylko do odczytu w tej prezentacji; w narzędziu walidacja odbywa się już na etapie wyboru.',
              },
              {
                title: 'Silnik logiczny',
                caption:
                  'Program dobiera ceny z bazy, nalicza rabaty i VAT, stosuje blokady logiczne uniemożliwiające wzajemnie wykluczające się opcje (np. sterowanie manualne z napędem elektrycznym), obsługuje przewalutowanie, generowanie ofert w innym języku oraz zapis i wczytywanie konfiguracji.',
              },
              {
                title: 'Generowanie oferty',
                caption:
                  'Generuje gotową ofertę w Wordzie: wypełnia tabele cenowe, wstawia zdjęcie techniczne dopasowane do konfiguracji i liczy podsumowania. Skrócił czas przygotowania oferty z kilkunastu lub kilkudziesięciu minut do około 30 sekund.',
              },
            ],
            ui: {
              deviceType: 'Typ urządzenia',
              capacity: 'Nośność',
              control: 'Sterowanie',
              controlManual: 'Manualne',
              controlElectric: 'Elektryczne',
              lockedHint: 'Zablokowane',
              flowParams: 'Parametry',
              flowEngine: 'Silnik',
              flowOutput: 'Wynik',
              flowPrice: 'Cena + VAT',
              flowLocks: 'Lista blokad',
              docHeader: 'Oferta handlowa',
              docTable: 'Pozycje i ceny',
              docImage: 'Zdjęcie techniczne',
              docTotal: 'Podsumowanie',
              deviceOption1: 'Urządzenie',
              deviceOption2: 'Żuraw magazynowy',
              capacityOption1: '500 kg',
              capacityOption2: '1000 kg',
            },
          },
        },
      ],
    },
    contact: {
      heading: 'Kontakt',
      subheading: 'Chcesz porozmawiać o projekcie lub współpracy? Napisz do mnie.',
      nameLabel: 'Imię i nazwisko',
      namePlaceholder: 'Twoje imię i nazwisko',
      nameError: 'Podaj swoje imię',
      contactLabel: 'Email / Telefon',
      contactPlaceholder: 'Adres email lub numer telefonu',
      contactError: 'Podaj email lub numer telefonu',
      messageLabel: 'Wiadomość',
      messagePlaceholder: 'Opisz w czym mogę Ci pomóc...',
      messageError: 'Wpisz treść wiadomości',
      sendButton: 'Wyślij wiadomość',
      successMessage: 'Wiadomość wysłana',
      errorMessage:
        'Coś poszło nie tak, spróbuj ponownie lub napisz na pawelrys175@gmail.com',
      directContact: 'Możesz również skontaktować się bezpośrednio:',
    },
    footer: {
      copyright: 'Paweł Ryś - Portfolio. Wszelkie prawa zastrzeżone.',
    },
  },
  en: {
    nav: {
      about: 'About',
      expertise: 'Skills',
      history: 'Experience',
      projects: 'Projects',
      contact: 'Contact',
      downloadCv: 'Download CV',
      cvUrl: '/projects/CV_Pawel_EN.pdf',
    },
    main: {
      name: 'Pawel Rys',
      roles: 'Web Developer · Next.js • WordPress • SEO',
      location: 'Near Bielsko-Biala, Poland',
      aboutShort: 'I build modern, optimized websites and business tools, combining clean code with analytics, SEO, and workflow automations.',
      downloadCv: 'Download CV (EN)',
      cvUrl: '/projects/CV_Pawel_EN.pdf',
      seeProjects: 'See projects',
      photoUrl: '/photo.jpg',
      social: {
        github: 'https://github.com/pawel799',
        linkedin: '',
        email: 'pawelrys175@gmail.com',
      },
    },
    expertise: {
      heading: 'Skills and Expertise',
      subtitle: 'Technologies and areas of specialization',
      techStackLabel: 'Tech stack:',
      items: [
        {
          iconType: 'code',
          title: 'Web Development',
          description: 'Designing and developing fast, modern websites and web applications with a focus on clean code, responsiveness, and performance optimization.',
          techStack: ['HTML', 'CSS', 'JavaScript', 'TypeScript', 'React', 'Next.js', 'Three.js / React Three Fiber', 'Tailwind CSS', 'WordPress', 'Decap CMS', 'PHP', 'MySQL', '.NET', 'SQL Server', 'Cloudflare'],
        },
        {
          iconType: 'server',
          title: 'Automations and IT Support',
          description: 'Business process automation, API integrations, internal tools development, and comprehensive IT systems and network administration.',
          techStack: ['Make (automations)', 'VBA', 'InsERT GT / Nexo', 'Networking, IP monitoring, backups'],
        },
        {
          iconType: 'chart',
          title: 'Digital Marketing and SEO',
          description: 'Effective search engine optimization (including local SEO), web analytics, structured data implementation, and Google Ads campaigns.',
          techStack: ['SEO', 'Local SEO', 'Google Ads', 'JSON-LD Structured Data', 'Web Analytics', 'Hreflang / Sitemap'],
        },
        {
          iconType: 'hosting',
          title: 'Hosting and Servers',
          description: 'Setting up hosting, domains and certificates, moving websites between servers, and automated deployments.',
          techStack: ['Linux', 'Windows Server', 'Plesk', 'cPanel', 'DNS / SSL', 'GitHub Actions'],
        },
      ],
    },
    featured: [{
      id: 'featured',
      eyebrow: 'Featured project',
      title: '3D Kitchen Quoting System',
      pitch: 'A kitchen offer the client explores in 3D in the browser, instead of a PDF and screenshots.',
      problemHeading: 'Problem',
      problem:
        "Kitchen studios and furniture showrooms send offers as PDFs and screenshots from their design software. Clients can't compare materials on their own, every front or countertop change means another message and a new quote, and the designer doesn't know whether the offer was even opened.",
      solutionHeading: 'Solution',
      solution:
        'A web app where the designer prepares an offer in a panel and sends the client a link. The client opens the kitchen in 3D on a phone or computer, swaps fronts, countertop, and hardware, sees the price right away, and leaves comments. The designer sees opens, comments, and the offer status in the panel.',
      roleLabel: 'My role',
      role: 'I designed and built everything: the panel and offer page UI, the 3D scene, the API on Cloudflare Pages Functions with storage in Workers KV, and the deployment.',
      featuresHeading: 'Key features',
      features: [
        'Offer panel with statuses (draft, sent, opened, changes requested, accepted), search, link revocation, and offer deletion.',
        'Client link with a random address, optional PIN, and expiry date; the price can be hidden per offer.',
        '3D configurator with swappable fronts, countertop, and hardware, with the price recalculated live.',
        'Client comments on the offer, open counter, and event history in the panel.',
        'Company data looked up by Polish tax ID (NIP) from the VAT register (Ministry of Finance) and the KRS API.',
        'HTML offer email template compatible with popular email clients.',
      ],
      techLabel: 'Stack',
      tech: ['Next.js 14', 'TypeScript', 'React Three Fiber', 'drei', 'postprocessing', 'Tailwind CSS', 'Cloudflare Pages Functions', 'Workers KV'],
      newTabHint: '(opens in a new tab)',
      galleryLabel: 'Screenshots of the quoting system',
      links: [
        { label: 'View website', url: 'https://system-ofertowania.pages.dev', kind: 'primary' },
        { label: '3D demo offer', url: 'https://system-ofertowania.pages.dev/oferta/demo', kind: 'secondary' },
        { label: 'GitHub', url: 'https://github.com/pawel799/System-ofertowania-', kind: 'github' },
      ],
      images: [
        {
          src: '/projects/system-ofertowania/oferta-3d.webp',
          alt: 'Client offer: kitchen in 3D with a bar for choosing fronts, countertop, and hardware, and the net price',
          caption: 'Client offer: kitchen in 3D, material choice, and live price',
        },
        {
          src: '/projects/system-ofertowania/panel.webp',
          alt: 'Offer panel: list of offers, 3D preview, and client link with expiry date and a ready message',
          caption: 'Panel: offer, client link, and ready message',
        },
        {
          src: '/projects/system-ofertowania/mail.webp',
          alt: 'Offer email: summary of materials, price estimate, and a button to the 3D kitchen',
          caption: 'Offer email with a button to the 3D kitchen',
        },
      ],
    },
    {
      id: 'featured-rox',
      eyebrow: 'Featured project',
      title: 'ROX Group: group website and 4 company sites',
      pitch: 'Five company websites in one project, with a panel where the client edits content themselves.',
      problemHeading: 'Problem',
      problem:
        'ROX Group has four companies with different profiles (trade, equipment rental, aluminium production, logistics). Each needed its own site on its own domain, the whole thing had to look consistent, and the client needed to edit it without a developer.',
      solutionHeading: 'Solution',
      solution:
        "A single Next.js project with static export that builds the group website and four company sites. The client edits content in the /admin panel (Decap CMS). The site first ran on Cloudflare Pages; I then moved it to the client's hosting with automatic deployment via GitHub Actions.",
      roleLabel: 'My role',
      role: "I built everything: page layout and design, content panel setup, the contact form, deployment, and the move to the client's hosting together with the domains.",
      featuresHeading: 'Key parts',
      features: [
        'Group website and four company sites (ROX Global, ROX Rent, ROX Aluminium, FLS Global) in one repository.',
        'Decap CMS: the client edits content in /admin, login via GitHub OAuth.',
        "Deployment to the client's hosting over FTP from GitHub Actions on every change in the repository.",
        'Separate company domains: aliases handled with Apache rewrite rules, old addresses redirected with 301.',
        'Contact form in PHP sending through SMTP (PHPMailer), protected with Cloudflare Turnstile.',
      ],
      techLabel: 'Stack',
      tech: ['Next.js', 'React', 'TypeScript', 'Tailwind CSS', 'Decap CMS', 'PHP', 'GitHub Actions'],
      newTabHint: '(opens in a new tab)',
      galleryLabel: 'Screenshots of the ROX Group websites',
      links: [
        { label: 'View website', url: 'https://gruparox.com', kind: 'primary' },
        { label: 'ROX Global', url: 'https://roxglobal.pl', kind: 'secondary' },
        { label: 'ROX Rent', url: 'https://roxrent.pl', kind: 'secondary' },
        { label: 'ROX Aluminium', url: 'https://roxaluminium.pl', kind: 'secondary' },
        { label: 'FLS Global', url: 'https://fls-global.pl', kind: 'secondary' },
      ],
      images: [
        { src: '/projects/rox-group/gruparox.webp', alt: 'ROX Group homepage: group headline and tiles for the four companies', caption: 'Group website with company selection' },
        { src: '/projects/rox-group/roxrent.webp', alt: 'ROX Rent website: construction equipment rental with a booking button', caption: 'ROX Rent' },
        { src: '/projects/rox-group/roxaluminium.webp', alt: 'ROX Aluminium website: photo of a building with an aluminium facade', caption: 'ROX Aluminium' },
        { src: '/projects/rox-group/admin-edycja.webp', alt: 'Decap CMS panel: page edit form with title, path, SEO description and company fields, plus a preview', caption: 'Editing a page in the /admin panel (Decap CMS)' },
      ],
    }],
    timeline: {
      heading: 'Experience and Education',
      items: [
        {
          type: 'work',
          title: 'Web Developer / Marketing',
          subtitle: 'Group of companies, including ROX Group',
          date: '09/2026 – present',
          points: [
            'Websites and online presence for a group of companies, including ROX Group: Next.js + Decap CMS (the client edits content themselves).',
            "Migration of the sites to the client's hosting (Plesk) with deployment via GitHub Actions (FTP).",
            '301 redirects across about 10 domains, SSL certificates, contact form sending through SMTP.',
          ],
        },
        {
          type: 'work',
          title: 'DS System',
          subtitle: '06/2025 – 08/2026',
          date: '06/2025 – 08/2026',
          points: [
            'Development and maintenance of B2B websites and internal tools (including a VBA offer configurator): e.g. 3D product model (Blender → Three.js with Draco compression), Fuse.js search, JSON-LD (Product), hreflang, sitemap; Next.js on Cloudflare Workers, Resend, Cloudflare Turnstile.',
            'Automations in Make: a scenario pulls events from Google Calendar and sends customers service reminders via SMS API.',
            'IT support: network device management (including mesh systems), printer repair and configuration.',
            'User support for InsERT software, plus configuration and data migration from InsERT GT to Nexo.',
          ],
        },
        {
          type: 'work',
          title: 'DUDIKOM - Computer Service',
          subtitle: 'Summer internship',
          date: '07/2024',
          points: [
            'IT and networking: IT systems administration, programming, network management, IP camera and monitoring system configuration.',
            'Websites and databases: building and optimizing websites (HTML, CSS, WordPress), SQL database management, IT security and backups.',
          ],
        },
        {
          type: 'work',
          title: 'SILV Computer Systems',
          subtitle: 'Internship',
          date: '04/2024',
          points: [
            'Building applications in the .NET environment, working with SQL Server databases.',
          ],
        },
        {
          type: 'work',
          title: 'M3.NET Sp. z o.o. (Bielsko-Biala)',
          subtitle: 'Internship',
          date: '10/2023',
          points: [
            'Involved in installing and configuring fiber-optic infrastructure.',
          ],
        },
        {
          type: 'work',
          title: 'Shell',
          subtitle: 'Junior Service Employee',
          date: '04/2023 – 06/2025',
          points: [
            'Customer service and cash register, help with deliveries, MS Office, product and stock management.',
          ],
        },
        {
          type: 'education',
          title: 'University of Bielsko-Biala',
          subtitle: 'Computer Science',
          date: '09/2025 – in progress',
          points: [
            'Courses include algorithms and data structures, databases (SQL), operating systems, computer architecture, discrete mathematics, algebra, probability and statistics, physics, UI/UX.',
          ],
        },
        {
          type: 'education',
          title: 'IT Technician',
          subtitle: 'Professional certificates INF.02 and INF.03',
          date: 'Education',
          points: [
            'Graduated as an IT Technician with INF.02 and INF.03 certifications.',
          ],
        },
      ],
    },
    projects: {
      heading: 'Projects',
      subtitle: 'Selected commercial websites and business software solutions',
      cursorLabel: 'View',
      viewProject: 'View Live',
      roleLabel: 'My role:',
      scopeLabel: 'Scope & stack:',
      items: [
        {
          id: 'ds-system',
          title: 'DS System',
          type: 'B2B Website / Next.js',
          role: 'Full B2B website build: architecture, language versions, technical SEO, deployment on Cloudflare Workers.',
          description:
            'Manufacturer of industrial vacuum lifters, cranes, and storage systems (CE, Poland). Multilingual website (EN, PL, CZ) with product catalog and inquiry forms. 3D product model built in Blender and rendered on-site with Three.js and Draco geometry compression (Google) for fast loading. Structured data: JSON-LD (Product schema), hreflang, sitemap. Search: Fuse.js fuzzy search, result highlighting, keyboard navigation, search history (localStorage). Stack: Next.js on Cloudflare Workers, Resend (contact form), Cloudflare Turnstile (anti-spam).',
          liveUrl: 'https://dssystem-machinery.com',
          image: '/projects/dssystem.jpg',
        },
        {
          id: 'igosport',
          title: 'IGOSPORT',
          type: 'Running Store / Local SEO · Next.js + Tailwind',
          role: 'Design and build of a running store website focused on local SEO.',
          description:
            'Specialist running store in Bielsko-Biala: gait analysis and running shoe selection (Saucony, Brooks, Diadora, Tecnica). Homepage, Events, Contact, local SEO (Bielsko-Biala), Google reviews, route map. Stack: Next.js, Tailwind CSS. Google Search Console results (27 May–Sep 2026): 490 clicks, 8.84k impressions, 5.5% CTR, average position 5.8 — a clear rise in clicks and impressions from early September 2026.',
          liveUrl: 'https://igosport.pl',
          image: '/projects/igosport.jpg',
          secondaryImage: '/projects/google-search.png',
          summary: 'Store website in Next.js and Tailwind CSS, plus local SEO for Bielsko-Biala.',
          note: 'Google Search Console, 12-month view: 490 clicks, 8.84k impressions, average position 5.8. Growth after local SEO and content optimisation.',
        },
        {
          id: 'vba-configurator',
          title: 'VBA Configurator - DS System',
          type: 'Internal Tool / VBA',
          role: 'Design and implementation of an automated offer configurator in VBA for DS System.',
          description:
            'An Excel VBA offer configurator integrated with Word. A salesperson picks the device type and parameters (load capacity, dimensions, power, control type) in a form; the tool pulls prices from a database, applies discounts and VAT, then generates a complete Word offer — filling price tables, inserting a matching technical image, and calculating totals. It includes logic locks that prevent incompatible option combinations (e.g. manual control with an electric drive), supports currency conversion and generating offers in another language, and can save/load quote configurations. Cut offer preparation time from 15–60 minutes down to about 30 seconds.',
          showcase: {
            kind: 'vba-configurator',
            intro:
              'An Excel VBA offer configurator integrated with Word: from parameter selection in a form to a finished Word quote.',
            steps: [
              {
                title: 'Configuration',
                caption:
                  'A salesperson picks the device type and parameters (load capacity, dimensions, power, control type) in a form. Fields are read-only in this demo; in the live tool, validation runs as options are chosen.',
              },
              {
                title: 'Logic engine',
                caption:
                  'The tool pulls prices from a database, applies discounts and VAT, enforces logic locks that block incompatible combinations (e.g. manual control with an electric drive), supports currency conversion, generating offers in another language, and saving/loading configurations.',
              },
              {
                title: 'Offer generation',
                caption:
                  'It generates a complete Word offer: filling price tables, inserting a matching technical image, and calculating totals. Cut offer preparation time from about 15-60 minutes to about 30 seconds.',
              },
            ],
            ui: {
              deviceType: 'Device type',
              capacity: 'Load capacity',
              control: 'Control',
              controlManual: 'Manual',
              controlElectric: 'Electric',
              lockedHint: 'Blocked',
              flowParams: 'Parameters',
              flowEngine: 'Engine',
              flowOutput: 'Output',
              flowPrice: 'Price + VAT',
              flowLocks: 'Lock list',
              docHeader: 'Commercial offer',
              docTable: 'Line items',
              docImage: 'Technical image',
              docTotal: 'Summary',
              deviceOption1: 'Device',
              deviceOption2: 'Storage crane',
              capacityOption1: '500 kg',
              capacityOption2: '1000 kg',
            },
          },
        },
      ],
    },
    contact: {
      heading: 'Contact',
      subheading: 'Looking to start a project or discuss collaboration? Send me a message.',
      nameLabel: 'Your Name',
      namePlaceholder: 'What is your name?',
      nameError: 'Please enter your name',
      contactLabel: 'Email / Phone',
      contactPlaceholder: 'How can I reach you?',
      contactError: 'Please enter your email or phone',
      messageLabel: 'Message',
      messagePlaceholder: 'Tell me about your project or inquiry...',
      messageError: 'Please enter your message',
      sendButton: 'Send Message',
      successMessage: 'Message sent',
      errorMessage:
        'Something went wrong. Please try again or email pawelrys175@gmail.com',
      directContact: 'You can also reach out directly via:',
    },
    footer: {
      copyright: 'Pawel Rys - Portfolio. All rights reserved.',
    },
  },
};
